// frontend/app/components/lessonPlans/lessonVideoSegmentStop.js
//
// Stopping a lesson clip at the end of its tagged segment.
//
// Extracted from `LessonVideoPlayer` for the same reason `lessonVideoSource`
// was: the component renders a raw <video> inside a react-native tree, so under
// jest there is no DOM element to drive and none of this would be tested. Here
// it is plain media-element handling and can be exercised against a fake.
//
// The behaviour is deliberately event-driven rather than armed once. A teacher
// scrubs backwards to re-explain a point far more often than anyone expects,
// and a stop that fired a single time would silently stop working for the rest
// of the lesson.

/**
 * Pause `element` when playback reaches `endSec`, and keep doing so.
 *
 * `onEnded` fires when the boundary is crossed; `onCleared` fires when the
 * teacher has moved back inside the segment or chosen to watch past it. Returns
 * a dispose function; calling it removes every listener.
 *
 * A non-finite `endSec` — including the null the backend sends for the last tag
 * on a video — attaches nothing at all, so the clip plays to the end of the
 * recording. That is a real answer, not a missing one.
 */
export function attachSegmentStop({ element, endSec, onEnded, onCleared }) {
  const noop = () => {};
  if (!element || typeof element.addEventListener !== "function") return noop;
  if (typeof endSec !== "number" || !Number.isFinite(endSec)) return noop;

  // Set once the teacher has been told the segment finished. It stays set while
  // they remain past the boundary, so that choosing to carry on is not undone
  // by the very next timeupdate.
  let announced = false;

  const currentTime = () => Number(element.currentTime) || 0;

  /** Back inside the segment: re-arm, so the boundary is enforced again. */
  const rearm = () => {
    if (!announced) return;
    announced = false;
    onCleared?.();
  };

  // `timeupdate` fires roughly four times a second, so the pause lands up to a
  // quarter-second late. Nobody in a classroom notices, and polling faster would
  // cost frames the Unity scenes elsewhere on the page need more.
  //
  // It re-arms as well as stops. Deriving that from position rather than from
  // the `seeked` event means a rewind is honoured however it happened — the
  // scrub bar, a keyboard shortcut, or the replay button — instead of depending
  // on one event reaching us.
  const onTimeUpdate = () => {
    if (currentTime() < endSec) {
      rearm();
      return;
    }
    if (announced) return;
    announced = true;
    try {
      element.pause?.();
    } catch {}
    onEnded?.();
  };

  // Seeking while paused may not produce a `timeupdate`, so re-arm here too.
  const onSeeked = () => {
    if (currentTime() >= endSec) return;
    rearm();
  };

  // Pressing play while already past the end is a deliberate choice to watch on.
  const onPlay = () => {
    if (currentTime() < endSec) return;
    announced = true;
    onCleared?.();
  };

  element.addEventListener("timeupdate", onTimeUpdate);
  element.addEventListener("seeked", onSeeked);
  element.addEventListener("play", onPlay);

  return () => {
    element.removeEventListener("timeupdate", onTimeUpdate);
    element.removeEventListener("seeked", onSeeked);
    element.removeEventListener("play", onPlay);
  };
}

export default attachSegmentStop;
